import React, {
    useEffect,
    useMemo,
    useState
} from 'react';
import './adminInquiry.css';
import API_BASE_URL from '../../../config/api';

const statusTabs = [
    {
        label: '전체',
        value: 'ALL'
    },
    {
        label: '신규',
        value: 'WAITING'
    },
    {
        label: '처리중',
        value: 'PROCESSING'
    },
    {
        label: '완료',
        value: 'COMPLETED'
    }
];

const statusInfo = {
    WAITING: {
        label: '신규',
        className: 'waiting'
    },
    PROCESSING: {
        label: '처리중',
        className: 'processing'
    },
    COMPLETED: {
        label: '완료',
        className: 'completed'
    }
};

const ITEMS_PER_PAGE = 10;

const AdminInquiryList = () => {
    const [inquiries, setInquiries] =
        useState([]);
    const [selectedStatus, setSelectedStatus] =
        useState('ALL');
    const [searchKeyword, setSearchKeyword] =
        useState('');
    const [currentPage, setCurrentPage] =
        useState(1);
    const [selectedInquiry, setSelectedInquiry] =
        useState(null);
    const [isLoading, setIsLoading] =
        useState(true);
    const [isDetailLoading, setIsDetailLoading] =
        useState(false);
    const [isStatusUpdating, setIsStatusUpdating] =
        useState(false);
    const [error, setError] =
        useState('');
    const [detailError, setDetailError] =
        useState('');

    /*
     * ================================================
     * 문의 목록 조회
     * ================================================
     */

    useEffect(() => {
        const fetchInquiries = async () => {
            try {
                setIsLoading(true);
                setError('');
                const response = await fetch(
                    `${API_BASE_URL}/api/inquiries`,
                    {
                        method: 'GET',
                        credentials: 'include'
                    }
                );
                const result =
                    await response.json();
                if (!response.ok) {
                    throw new Error(
                        result.message ||
                        '견적문의를 불러오지 못했습니다.'
                    );
                }
                setInquiries(
                    result.inquiries || []
                );
            } catch (error) {
                console.error(
                    '견적문의 조회 오류:',
                    error
                );
                setError(
                    error.message
                );
            } finally {
                setIsLoading(false);
            }
        };
        fetchInquiries();
    }, []);

    /*
     * ================================================
     * MODAL BODY SCROLL
     * ================================================
     */

    useEffect(() => {
        if (
            selectedInquiry ||
            isDetailLoading ||
            detailError
        ) {
            document.body.style.overflow =
                'hidden';
        } else {
            document.body.style.overflow =
                '';
        }
        return () => {
            document.body.style.overflow =
                '';
        };
    }, [
        selectedInquiry,
        isDetailLoading,
        detailError
    ]);

    /*
     * ================================================
     * FILTER
     * ================================================
     */

    const filteredInquiries =
        useMemo(() => {
            const keyword =
                searchKeyword
                    .trim()
                    .toLowerCase();
            return inquiries.filter(
                (inquiry) => {
                    const matchesStatus =
                        selectedStatus === 'ALL' ||
                        inquiry.status ===
                        selectedStatus;
                    if (!matchesStatus) {
                        return false;
                    }
                    if (!keyword) {
                        return true;
                    }
                    const searchableText = [
                        inquiry.name,
                        inquiry.phone,
                        inquiry.email,
                        inquiry.equipment,
                        inquiry.event_type,
                        inquiry.event_date,
                        inquiry.location,
                        inquiry.scale,
                        inquiry.content,
                        inquiry.created_at
                    ]
                        .filter(Boolean)
                        .join(' ')
                        .toLowerCase();
                    return searchableText.includes(
                        keyword
                    );
                }
            );
        }, [
            inquiries,
            selectedStatus,
            searchKeyword
        ]);
    const handleStatusFilterChange = (status) => {
        setSelectedStatus(status);
        setCurrentPage(1);
    };
    const handleSearchChange = (event) => {
        setSearchKeyword(
            event.target.value
        );
        setCurrentPage(1);
    };
    const handleClearSearch = () => {
        setSearchKeyword('');
        setCurrentPage(1);
    };
    const totalPages = Math.max(
        1,
        Math.ceil(
            filteredInquiries.length /
            ITEMS_PER_PAGE
        )
    );
    const paginatedInquiries =
        useMemo(() => {
            const startIndex =
                (currentPage - 1) *
                ITEMS_PER_PAGE;
            const endIndex =
                startIndex +
                ITEMS_PER_PAGE;
            return filteredInquiries.slice(
                startIndex,
                endIndex
            );
        }, [
            filteredInquiries,
            currentPage
        ]);
    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(
                totalPages
            );
        }
    }, [
        currentPage,
        totalPages
    ]);
    const pageNumbers = useMemo(() => {
        if (totalPages <= 5) {
            return Array.from(
                {
                    length: totalPages
                },
                (_, index) =>
                    index + 1
            );
        }
        let startPage = Math.max(
            1,
            currentPage - 2
        );
        const endPage = Math.min(
            totalPages,
            startPage + 4
        );
        if (endPage - startPage < 4) {
            startPage = Math.max(
                1,
                endPage - 4
            );
        }
        return Array.from(
            {
                length:
                    endPage - startPage + 1
            },
            (_, index) =>
                startPage + index
        );
    }, [
        currentPage,
        totalPages
    ]);

    /*
     * ================================================
     * COUNT
     * ================================================
     */

    const getStatusCount = (status) => {
        if (status === 'ALL') {
            return inquiries.length;
        }
        return inquiries.filter(
            (inquiry) =>
                inquiry.status === status
        ).length;
    };

    /*
     * ================================================
     * DATE
     * ================================================
     */

    const formatDate = (date) => {
        if (!date) {
            return '-';
        }
        return new Intl.DateTimeFormat(
            'ko-KR',
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }
        ).format(
            new Date(date)
        );
    };
    const formatDateTime = (date) => {
        if (!date) {
            return '-';
        }
        return new Intl.DateTimeFormat(
            'ko-KR',
            {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            }
        ).format(
            new Date(date)
        );
    };

    /*
     * ================================================
     * STATUS
     * ================================================
     */

    const getStatusInfo = (status) => {
        return (
            statusInfo[status] || {
                label: status,
                className: ''
            }
        );
    };

    /*
     * ================================================
     * DETAIL
     * ================================================
     */

    const handleView = async (
        inquiryId
    ) => {
        try {
            setIsDetailLoading(true);
            setDetailError('');
            setSelectedInquiry(null);
            const response = await fetch(
                `${API_BASE_URL}/api/inquiries/${inquiryId}`,
                {
                    method: 'GET',
                    credentials: 'include'
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '견적문의를 불러오지 못했습니다.'
                );
            }
            setSelectedInquiry(
                result.inquiry
            );
        } catch (error) {
            console.error(
                '견적문의 상세 조회 오류:',
                error
            );
            setDetailError(
                error.message
            );
        } finally {
            setIsDetailLoading(false);
        }
    };
    const handleCloseDetail = () => {
        setSelectedInquiry(null);
        setDetailError('');
    };

    /*
     * ================================================
     * STATUS UPDATE
     * ================================================
     */

    const handleStatusChange = async (
        status
    ) => {
        if (
            !selectedInquiry ||
            selectedInquiry.status === status
        ) {
            return;
        }
        try {
            setIsStatusUpdating(true);
            const response = await fetch(
                `${API_BASE_URL}/api/inquiries/${selectedInquiry.inquiry_id}/status`,
                {
                    method: 'PATCH',
                    headers: {
                        'Content-Type':
                            'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                        status
                    })
                }
            );
            const result =
                await response.json();
            if (!response.ok) {
                throw new Error(
                    result.message ||
                    '문의 상태 변경에 실패했습니다.'
                );
            }
            setSelectedInquiry(
                result.inquiry
            );
            setInquiries((prev) =>
                prev.map((inquiry) =>
                    inquiry.inquiry_id ===
                    result.inquiry.inquiry_id
                        ? {
                            ...inquiry,
                            status:
                                result.inquiry.status,
                            updated_at:
                                result.inquiry.updated_at
                        }
                        : inquiry
                )
            );
        } catch (error) {
            console.error(
                '문의 상태 변경 오류:',
                error
            );
            alert(
                error.message
            );
        } finally {
            setIsStatusUpdating(false);
        }
    };
    return (
        <div className="admin-inquiry">
            {/* HEADER */}
            <div className="admin-page-header">
                <div>
                    <p>
                        INQUIRY
                    </p>
                    <h1>
                        견적문의 관리
                    </h1>
                    <span>
                        고객이 접수한 견적문의와 상담 진행 상태를 관리합니다.
                    </span>
                </div>
            </div>
            {/* CONTENT */}
            <div className="admin-inquiry-content">
                {/* STATUS TAB + SEARCH */}
                <div className="admin-inquiry-toolbar">
                    <div className="admin-inquiry-tabs">
                        {statusTabs.map(
                            (tab) => (
                                <button
                                    type="button"
                                    key={tab.value}
                                    className={
                                        selectedStatus ===
                                        tab.value
                                            ? 'active'
                                            : ''
                                    }
                                    onClick={() =>
                                        handleStatusFilterChange(
                                            tab.value
                                        )
                                    }
                                >
                                    {tab.label}
                                    <span>
                                        {getStatusCount(
                                            tab.value
                                        )}
                                    </span>
                                </button>
                            )
                        )}
                    </div>
                    <div className="admin-inquiry-search-area">
                        <span className="admin-inquiry-result-count">
                            {filteredInquiries.length}건
                        </span>
                        <div className="admin-inquiry-search">
                            <i className="mdi mdi-magnify"></i>
                            <input
                                type="text"
                                value={searchKeyword}
                                onChange={handleSearchChange}
                                placeholder="문의자, 연락처, 장비, 지역 검색"
                                aria-label="견적문의 검색"
                            />
                            {searchKeyword && (
                                <button
                                    type="button"
                                    className="admin-inquiry-search-clear"
                                    onClick={handleClearSearch}
                                    aria-label="검색어 지우기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/* LOADING */}
                {isLoading ? (
                    <div className="admin-inquiry-empty">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            견적문의를 불러오는 중입니다.
                        </strong>
                    </div>
                ) : error ? (
                    /* ERROR */
                    <div className="admin-inquiry-empty">
                        <i className="mdi mdi-alert-circle-outline"></i>
                        <strong>
                            견적문의를 불러오지 못했습니다.
                        </strong>
                        <p>
                            {error}
                        </p>
                    </div>
                ) : filteredInquiries.length === 0 ? (
                    /* EMPTY */
                    <div className="admin-inquiry-empty">
                        <i className="mdi mdi-email-outline"></i>
                        <strong>
                            등록된 견적문의가 없습니다.
                        </strong>
                        <p>
                            해당 상태의 문의가 없습니다.
                        </p>
                    </div>
                ) : (
                    /* TABLE */
                    <div className="admin-inquiry-results">
                        <div className="admin-inquiry-table-wrap">
                        <table className="admin-inquiry-table">
                            <thead>
                                <tr>
                                    <th>
                                        문의번호
                                    </th>
                                    <th>
                                        문의자
                                    </th>
                                    <th>
                                        문의장비
                                    </th>
                                    <th>
                                        행사일
                                    </th>
                                    <th>
                                        지역
                                    </th>
                                    <th>
                                        문의일
                                    </th>
                                    <th>
                                        상태
                                    </th>
                                    <th>
                                        관리
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedInquiries.map(
                                    (inquiry) => {
                                        const status =
                                            getStatusInfo(
                                                inquiry.status
                                            );
                                        return (
                                            <tr
                                                key={
                                                    inquiry.inquiry_id
                                                }
                                            >
                                                <td>
                                                    #
                                                    {
                                                        inquiry.inquiry_id
                                                    }
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="admin-inquiry-name"
                                                        onClick={() =>
                                                            handleView(
                                                                inquiry.inquiry_id
                                                            )
                                                        }
                                                    >
                                                        {
                                                            inquiry.name
                                                        }
                                                    </button>
                                                    <span className="admin-inquiry-phone">
                                                        {
                                                            inquiry.phone
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    {
                                                        inquiry.equipment
                                                    }
                                                </td>
                                                <td>
                                                    {formatDate(
                                                        inquiry.event_date
                                                    )}
                                                </td>
                                                <td>
                                                    {
                                                        inquiry.location ||
                                                        '-'
                                                    }
                                                </td>
                                                <td>
                                                    {formatDate(
                                                        inquiry.created_at
                                                    )}
                                                </td>
                                                <td>
                                                    <span
                                                        className={
                                                            `admin-inquiry-status ${status.className}`
                                                        }
                                                    >
                                                        {
                                                            status.label
                                                        }
                                                    </span>
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="admin-inquiry-view"
                                                        onClick={() =>
                                                            handleView(
                                                                inquiry.inquiry_id
                                                            )
                                                        }
                                                    >
                                                        보기
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    }
                                )}
                            </tbody>
                        </table>
                    </div>
                        {totalPages > 1 && (
                            <div className="admin-inquiry-pagination">
                                <button
                                    type="button"
                                    className="admin-inquiry-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.max(
                                                    prev - 1,
                                                    1
                                                )
                                        )
                                    }
                                    disabled={currentPage === 1}
                                    aria-label="이전 페이지"
                                >
                                    <i className="mdi mdi-chevron-left"></i>
                                </button>
                                <div className="admin-inquiry-pagination-pages">
                                    {pageNumbers.map(
                                        (page) => (
                                            <button
                                                key={page}
                                                type="button"
                                                className={
                                                    currentPage === page
                                                        ? 'active'
                                                        : ''
                                                }
                                                onClick={() =>
                                                    setCurrentPage(
                                                        page
                                                    )
                                                }
                                            >
                                                {page}
                                            </button>
                                        )
                                    )}
                                </div>
                                <button
                                    type="button"
                                    className="admin-inquiry-pagination-arrow"
                                    onClick={() =>
                                        setCurrentPage(
                                            (prev) =>
                                                Math.min(
                                                    prev + 1,
                                                    totalPages
                                                )
                                        )
                                    }
                                    disabled={
                                        currentPage ===
                                        totalPages
                                    }
                                    aria-label="다음 페이지"
                                >
                                    <i className="mdi mdi-chevron-right"></i>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {/* DETAIL LOADING */}
            {isDetailLoading && (
                <div className="admin-inquiry-modal-overlay">
                    <div className="admin-inquiry-modal-loading">
                        <i className="mdi mdi-loading mdi-spin"></i>
                        <strong>
                            문의 내용을 불러오는 중입니다.
                        </strong>
                    </div>
                </div>
            )}
            {/* DETAIL ERROR */}
            {!isDetailLoading &&
                detailError && (
                    <div
                        className="admin-inquiry-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-inquiry-modal-loading"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <i className="mdi mdi-alert-circle-outline"></i>
                            <strong>
                                문의 내용을 불러오지 못했습니다.
                            </strong>
                            <p>
                                {detailError}
                            </p>
                            <button
                                type="button"
                                onClick={handleCloseDetail}
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                )}
            {/* DETAIL MODAL */}
            {!isDetailLoading &&
                selectedInquiry && (
                    <div
                        className="admin-inquiry-modal-overlay"
                        onClick={handleCloseDetail}
                    >
                        <div
                            className="admin-inquiry-modal"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            {/* MODAL HEADER */}
                            <div className="admin-inquiry-modal-header">
                                <div>
                                    <p>
                                        INQUIRY DETAIL
                                    </p>
                                    <h2>
                                        {
                                            selectedInquiry.name
                                        }
                                        님의 견적문의
                                    </h2>
                                </div>
                                <button
                                    type="button"
                                    className="admin-inquiry-modal-close"
                                    onClick={
                                        handleCloseDetail
                                    }
                                    aria-label="닫기"
                                >
                                    <i className="mdi mdi-close"></i>
                                </button>
                            </div>
                            {/* MODAL BODY */}
                            <div className="admin-inquiry-modal-body">
                                {/* STATUS */}
                                <div className="admin-inquiry-modal-status">
                                    <div>
                                        <span>
                                            현재 상태
                                        </span>
                                        <strong
                                            className={
                                                `admin-inquiry-current-status ${
                                                    getStatusInfo(
                                                        selectedInquiry.status
                                                    ).className
                                                }`
                                            }
                                        >
                                            {
                                                getStatusInfo(
                                                    selectedInquiry.status
                                                ).label
                                            }
                                        </strong>
                                    </div>
                                    <div className="admin-inquiry-status-actions">
                                        <button
                                            type="button"
                                            className={
                                                selectedInquiry.status ===
                                                'WAITING'
                                                    ? 'active waiting'
                                                    : ''
                                            }
                                            disabled={
                                                isStatusUpdating
                                            }
                                            onClick={() =>
                                                handleStatusChange(
                                                    'WAITING'
                                                )
                                            }
                                        >
                                            신규
                                        </button>
                                        <button
                                            type="button"
                                            className={
                                                selectedInquiry.status ===
                                                'PROCESSING'
                                                    ? 'active processing'
                                                    : ''
                                            }
                                            disabled={
                                                isStatusUpdating
                                            }
                                            onClick={() =>
                                                handleStatusChange(
                                                    'PROCESSING'
                                                )
                                            }
                                        >
                                            처리중
                                        </button>
                                        <button
                                            type="button"
                                            className={
                                                selectedInquiry.status ===
                                                'COMPLETED'
                                                    ? 'active completed'
                                                    : ''
                                            }
                                            disabled={
                                                isStatusUpdating
                                            }
                                            onClick={() =>
                                                handleStatusChange(
                                                    'COMPLETED'
                                                )
                                            }
                                        >
                                            완료
                                        </button>
                                    </div>
                                </div>
                                {/* CUSTOMER */}
                                <div className="admin-inquiry-modal-section">
                                    <div className="admin-inquiry-modal-section-title">
                                        고객 정보
                                    </div>
                                    <div className="admin-inquiry-detail-grid">
                                        <DetailRow
                                            label="이름"
                                            value={
                                                selectedInquiry.name
                                            }
                                        />
                                        <DetailRow
                                            label="전화번호"
                                            value={
                                                selectedInquiry.phone
                                            }
                                        />
                                        <DetailRow
                                            label="이메일"
                                            value={
                                                selectedInquiry.email
                                            }
                                        />
                                        <DetailRow
                                            label="문의일"
                                            value={
                                                formatDateTime(
                                                    selectedInquiry.created_at
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                {/* EVENT */}
                                <div className="admin-inquiry-modal-section">
                                    <div className="admin-inquiry-modal-section-title">
                                        행사 정보
                                    </div>
                                    <div className="admin-inquiry-detail-grid">
                                        <DetailRow
                                            label="문의장비"
                                            value={
                                                selectedInquiry.equipment
                                            }
                                        />
                                        <DetailRow
                                            label="행사일"
                                            value={
                                                formatDate(
                                                    selectedInquiry.event_date
                                                )
                                            }
                                        />
                                        <DetailRow
                                            label="행사장소"
                                            value={
                                                selectedInquiry.location
                                            }
                                        />
                                        <DetailRow
                                            label="행사종류"
                                            value={
                                                selectedInquiry.event_type
                                            }
                                        />
                                        <DetailRow
                                            label="행사규모"
                                            value={
                                                selectedInquiry.scale
                                            }
                                        />
                                    </div>
                                </div>
                                {/* CONTENT */}
                                <div className="admin-inquiry-modal-section">
                                    <div className="admin-inquiry-modal-section-title">
                                        문의 내용
                                    </div>
                                    <div className="admin-inquiry-content-box">
                                        {
                                            selectedInquiry.content
                                        }
                                    </div>
                                </div>
                            </div>
                            {/* FOOTER */}
                            <div className="admin-inquiry-modal-footer">
                                <a
                                    href={`tel:${selectedInquiry.phone}`}
                                    className="admin-inquiry-call"
                                >
                                    <i className="mdi mdi-phone-outline"></i>
                                    전화하기
                                </a>
                                {selectedInquiry.email && (
                                    <a
                                        href={`mailto:${selectedInquiry.email}`}
                                        className="admin-inquiry-mail"
                                    >
                                        <i className="mdi mdi-email-outline"></i>
                                        이메일
                                    </a>
                                )}
                                <button
                                    type="button"
                                    className="admin-inquiry-modal-cancel"
                                    onClick={
                                        handleCloseDetail
                                    }
                                >
                                    닫기
                                </button>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};

const DetailRow = ({
    label,
    value
}) => {
    return (
        <div className="admin-inquiry-detail-row">
            <strong>
                {label}
            </strong>
            <span>
                {value !== null &&
                value !== undefined &&
                value !== ''
                    ? value
                    : '-'
                }
            </span>
        </div>
    );
};
export default AdminInquiryList;
